import { Parameter } from 'app/model/base/parameter';
import { IValidatorFn } from 'app/interfaces/IValidatorFn';
import { RequiredValidatorFactoryService } from 'app/extensibility/services/validator-factory/required-validator-factory.service';
import { IntegerValidatorFactoryService } from 'app/extensibility/services/validator-factory/integer-validator-factory.service';

import { NumberValidatorFactoryService } from 'app/extensibility/services/validator-factory/number-validator-factory.service';
import { PatternValidatorFactoryService } from 'app/extensibility/services/validator-factory/pattern-validator-factory.service';
import {
    Named,
    IAttribute,
    AdapterContext,
    shellStringResources,
    Pipeline,
    LinkedService,
    Constants, IResourceContext
} from 'shell-models-lib';
import { stringIsNullOrEmpty } from './string.util';
import { ParameterValue } from 'app/model/base/parameter-value';
import { IParameterAdhocValues } from 'app/services/parameter-adhoc-value.service';
import { fillParameterValuesForResource, convertParameterValue } from './parameter-converter.util';
import { getLogicAppParametersFromExpression, isExpressionObject, isLogicAppExpression } from './expression.util';
import { DuplicatesValidatorFactoryService } from 'app/extensibility/services/validator-factory/duplicates-validator-factory.service';
import { Factory } from 'app/model/factory/factory';
import stringify from 'json-stable-stringify';
import { LoggerService } from 'app/logger/logger.service';
import { getCachedServiceInstanceSync } from 'app/multi-contexts/modules/root/root.application';

interface IParametersAndRunParameters {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    parameters: { [parameterName: string]: any };
    runParameters: { [parameterName: string]: any };
    /* eslint-enable @typescript-eslint/no-explicit-any */
}

const globalValidators = {
    requiredValidator: new RequiredValidatorFactoryService().createValidator(),
    integerValidator: new IntegerValidatorFactoryService().createValidator(undefined, undefined),
    floatValidator: new NumberValidatorFactoryService().createValidator(undefined, undefined),
    booleanValidator: new PatternValidatorFactoryService().createValidator(
        /^(true|false|)$/,
        stringResources.Validator_ErrorMessage_Boolean
    )
};

export function getParameterFieldValidators(parameterType: string, required: boolean = true): IValidatorFn[] {
    // fix top error: Cannot read property 'toLowerCase' of undefined
    if (!parameterType) {
        return [];
    }
    // securestring global parameters will allow empty values (pipeline parameters will not run this since validateFields = false in parameters-base.component.ts)
    const validators = required && parameterType !== 'securestring' ? [globalValidators.requiredValidator] : [];
    switch (parameterType.toLowerCase()) {
        case 'int':
            return validators.concat(globalValidators.integerValidator);
        case 'float':
            return validators.concat(globalValidators.floatValidator);
        case 'bool':
            return validators.concat(globalValidators.booleanValidator);
        case 'array':
            return validators.concat((value: string): string | null => {
                if (!value) {
                    return null;
                }

                try {
                    const defaultArray = JSON.parse(String(value));

                    if (!Array.isArray(defaultArray)) {
                        return shellStringResources.Parameterization_ValidArrayErrorMessage;
                    }

                    return null;
                } catch (error) {
                    return stringResources.InvalidValue;
                }
            });
        default:
            return validators;
    }
}

export function getParameterNameInvalidCharValidator(entity: Named): IValidatorFn {
    // according to https://learn.microsoft.com/en-us/azure/data-factory/naming-rules, best to follow naming rules of dataflow transformation.
    // however in LS, test connection succeeded with spaced parameterized name, thus making space a valid character in LS
    let regex: RegExp;
    let msg: string;
    if (entity instanceof LinkedService) {
        regex = Constants.alphaNumericUnderscoreSpaceValidation;
        msg = stringResources.Name_OnlyAlphanumericUnderscoreSpace_Message;
    } else {
        regex = Constants.alphaNumericUnderscoreValidation;
        msg = stringResources.Lakehouse_Name_InvalidCharacters_Message;
    }
    return new PatternValidatorFactoryService().createValidator(regex, msg);
}

export function getParameterNameValidators(owner: Factory | Pipeline | LinkedService): IValidatorFn[] {
    const parameterNames: string[] = [];
    owner.parameters.forEach((p) => parameterNames.push(p.name));
    const isDuplicateCondition = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
    return [
        globalValidators.requiredValidator,
        new DuplicatesValidatorFactoryService().createValidator(parameterNames, isDuplicateCondition)
    ];
}

export function valueAndExpressionValueBothEmpty(entity: Named, attr: IAttribute): boolean {
    return stringIsNullOrEmpty(attr.value(entity)) && stringIsNullOrEmpty(attr.templateValue(entity));
}

export function generateParametersAndRunParameters(
    contextData: IResourceContext,
    parameters: ParameterValue[],
    parameterNames: string[],
    parameterAdhocValues: IParameterAdhocValues
): IParametersAndRunParameters {
    const result: IParametersAndRunParameters = { parameters: {}, runParameters: {} };
    if (parameters != null) {
        // fill parameters' value in resource
        result.parameters = fillParameterValuesForResource(parameters, AdapterContext.defaultContextOf(contextData),
        getCachedServiceInstanceSync<LoggerService>(contextData, 'LoggerService'));
    }
    if (parameterNames != null && parameterAdhocValues != null) {
        // create runParameters according to backend-required format
        for (const paramName of parameterNames) {
            if (stringIsNullOrEmpty(paramName)) {
                continue;
            }
            if (paramName[0] === '@') {
                const splits = paramName.split('.');
                const indexOfLeftParenthese = splits[0].indexOf('(');
                const indexOfRightParenthese = splits[0].indexOf(')');
                let key = splits[0].substring(1, indexOfLeftParenthese);
                if (key === 'item' && splits.length === 1) {
                    result.runParameters[key] = convertParameterValue(
                        parameterAdhocValues[paramName].value,
                        parameterAdhocValues[paramName].type,
                        'ParameterUtil',
                        getCachedServiceInstanceSync<LoggerService>(contextData, 'LoggerService')
                    );
                    continue;
                }
                if (result.runParameters[key] == null) {
                    result.runParameters[key] = {};
                }
                let tmpObj = result.runParameters[key];
                if (indexOfLeftParenthese + 1 !== indexOfRightParenthese) {
                    key = splits[0].substring(indexOfLeftParenthese + 2, indexOfRightParenthese - 1);
                    if (splits.length === 1) {
                        tmpObj[key] = convertParameterValue(
                            parameterAdhocValues[paramName].value,
                            parameterAdhocValues[paramName].type,
                            'ParameterUtil',
                            getCachedServiceInstanceSync<LoggerService>(contextData, 'LoggerService')
                        );
                        continue;
                    } else if (tmpObj[key] == null) {
                        tmpObj[key] = {};
                    }
                    tmpObj = tmpObj[key];
                }
                for (let idx = 1; idx < splits.length - 1; ++idx) {
                    if (tmpObj[splits[idx]] == null) {
                        tmpObj[splits[idx]] = {};
                    }
                    tmpObj = tmpObj[splits[idx]];
                }
                tmpObj[splits[splits.length - 1]] =
                    parameterAdhocValues[splits[splits.length - 1]] != null
                        ? convertParameterValue(
                              parameterAdhocValues[splits[splits.length - 1]].value,
                              parameterAdhocValues[splits[splits.length - 1]].type,
                              'ParameterUtil',
                              getCachedServiceInstanceSync<LoggerService>(contextData, 'LoggerService')
                          )
                        : parameterAdhocValues[paramName] != null
                        ? convertParameterValue(
                              parameterAdhocValues[paramName].value,
                              parameterAdhocValues[paramName].type,
                              'ParameterUtil',
                              getCachedServiceInstanceSync<LoggerService>(contextData, 'LoggerService')
                          )
                        : undefined;
            } else {
                result.runParameters[paramName] =
                    parameterAdhocValues[paramName] != null
                        ? convertParameterValue(
                              parameterAdhocValues[paramName].value,
                              parameterAdhocValues[paramName].type,
                              'ParameterUtil',
                              getCachedServiceInstanceSync<LoggerService>(contextData, 'LoggerService')
                          )
                        : undefined;
            }
        }
    }
    return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function digestDataAsync(...data: any[]): string {
    const str = data.map((e) => stringify(e)).join('|');
    return str;
}

export function extractParametersFromParameterValues(parameterValues: ParameterValue[]): string[] {
    if (!parameterValues || parameterValues.length === 0) {
        return [];
    }
    const result: Set<string> = new Set<string>();
    parameterValues.forEach((paramValue) => {
        let templateValue = ParameterValue.VALUE.templateValue(paramValue);
        if (stringIsNullOrEmpty(templateValue) && isLogicAppExpression(paramValue.value)) {
            templateValue = paramValue.value;
        }
        const parameters = getLogicAppParametersFromExpression(templateValue);
        if (parameters && parameters.length > 0) {
            parameters.forEach((param) => {
                result.add(param);
            });
        }
    });
    return result.size > 0 ? Array.from(result) : [];
}

export function constructParameterValuesFromParameters(
    parameters: Parameter[],
    parameterAdhocValues: IParameterAdhocValues
): ParameterValue[] {
    if (!parameters || parameters.length === 0 || !parameterAdhocValues) {
        return [];
    }
    const result: ParameterValue[] = [];
    parameters.forEach((param) => {
        const paramValue = new ParameterValue();
        paramValue.parameter = param;
        paramValue.valueType = param._type;
        paramValue.value = parameterAdhocValues[param.name].value;
        result.push(paramValue);
    });
    return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractParametersFromResource(resource: any): string[] {
    if (typeof resource !== 'object') {
        return [];
    }
    const result: Set<string> = new Set<string>();
    for (const propertyName of Object.getOwnPropertyNames(resource)) {
        extractParametersFromObject(resource[propertyName], result);
    }
    return Array.from(result);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractParametersFromObject(obj: any, result: Set<string>): void {
    if (obj == null) {
        return;
    }
    if (Array.isArray(obj)) {
        obj.forEach((val) => extractParametersFromObject(val, result));
    } else if (typeof obj === 'object') {
        if (isExpressionObject(obj)) {
            const params = getLogicAppParametersFromExpression(obj.value);
            if (params && params.length > 0) {
                params.forEach((param) => result.add(param));
            }
            return;
        } else {
            for (const propertyName of Object.getOwnPropertyNames(obj)) {
                extractParametersFromObject(obj[propertyName], result);
            }
        }
    } else if (typeof obj === 'string') {
        if (isLogicAppExpression(obj)) {
            const params = getLogicAppParametersFromExpression(obj);
            if (params && params.length > 0) {
                params.forEach((param) => result.add(param));
            }
        }
    }
}
